import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { baseUrlApi } from '../../utils/url';

interface User {
  id: number | null;
  email: string;
  name?: string;
  firstName?: string;
  token: string;
  isConnected: boolean;
  isAdmin?: boolean;
  roles?: string[];
  OwnerId?: number | null;
  expiresAt?: string | null;
}

interface AuthState {
  user: User;
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: {
    id: null,
    email: '',
    name: '',
    firstName: '',
    token: '',
    isConnected: false,
    isAdmin: false,
    roles: [],
    OwnerId: null,
    expiresAt: null,
  },
  loading: false,
  error: null,
};

export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials: { email: string; password: string; rememberMe?: boolean }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${baseUrlApi}/auth/signin`, credentials);
      const backendUser = response.data.user;
      const token = response.data.token;
      const expiresAt = response.data.expiresAt || null;

      if (!backendUser?.id) return rejectWithValue('Données utilisateur invalides');

      const userData: User = {
        id: backendUser.id,
        email: backendUser.email,
        name: backendUser.name || '',
        firstName: backendUser.first_name || '',
        token,
        isConnected: true,
        isAdmin: backendUser.isAdmin || false,
        roles: [],
        OwnerId: backendUser.OwnerId || null,
        expiresAt,
      };

      localStorage.setItem('user', JSON.stringify(userData));
      return userData;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erreur de connexion');
    }
  }
);

export const registerUser = createAsyncThunk(
  'auth/register',
  async (
    userData: { name: string; first_name: string; email: string; password: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post(`${baseUrlApi}/auth/signup`, userData);
      return response.data; // { success: true, message: '...' }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Erreur lors de l'inscription");
    }
  }
);

export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${baseUrlApi}/auth/forgot-password`, { email });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erreur serveur');
    }
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async ({ token, password }: { token: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${baseUrlApi}/auth/reset-password/${token}`, { password });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Lien invalide ou expiré.');
    }
  }
);

export const logoutUser = createAsyncThunk('auth/logout', async (_, { dispatch }) => {
  localStorage.removeItem('user');
  dispatch({ type: 'RESET_STORE' });
  return null;
});

const authentificationSlice = createSlice({
  name: 'authentification',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(loginUser.fulfilled, (state, action) => { state.loading = false; state.user = action.payload; state.error = null; })
      .addCase(loginUser.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })

      .addCase(registerUser.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(registerUser.fulfilled, (state) => { state.loading = false; state.error = null; })
      .addCase(registerUser.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })

      .addCase(forgotPassword.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(forgotPassword.fulfilled, (state) => { state.loading = false; })
      .addCase(forgotPassword.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })

      .addCase(resetPassword.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(resetPassword.fulfilled, (state) => { state.loading = false; })
      .addCase(resetPassword.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })

      .addCase(logoutUser.fulfilled, (state) => { state.user = initialState.user; state.error = null; });
  },
});

export const { setUser, clearError } = authentificationSlice.actions;
export default authentificationSlice.reducer;
